// MemoryGraphView — DEM-64.
//
// Live force-directed graph of the unified-brain cross-link web. Extracted
// from KnowledgeBaseListPage so /memory's Index tab and the (legacy) Knowledge
// Base index can share the same component. Data is /api/kb-graph: pages +
// folders as nodes, slug-tree edges + cross-link edges.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ForceGraph2D from "react-force-graph-2d";
import { useApi } from "../../../lib/api";

type GNode = { id: string; label: string; group: string; kind: "page" | "folder" };
type GLink = { source: string; target: string; kind: "tree" | "xref" };

export function MemoryGraphView() {
  const api = useApi();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<{ nodes: GNode[]; links: GLink[] } | null>(null);
  const [stats, setStats] = useState<{ pages: number; folders: number; xrefs: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dims, setDims] = useState({ w: 800, h: 560 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api("/api/kb-graph");
        if (!res.ok) throw new Error(`/api/kb-graph ${res.status}`);
        const d = (await res.json()) as {
          nodes: GNode[];
          links: GLink[];
          stats?: { pages: number; folders: number; xrefs: number };
        };
        if (alive) {
          setData({ nodes: d.nodes ?? [], links: d.links ?? [] });
          setStats(d.stats ?? null);
        }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setDims({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      {stats && (
        <p className="mb-2 text-xs text-muted-foreground">
          {stats.pages} pages · {stats.folders} folders · {stats.xrefs} cross-links — click a node to open it.
        </p>
      )}
      <div ref={wrapRef} className="h-[70vh] w-full overflow-hidden rounded-xl border bg-card">
        {err && <p className="p-4 text-sm text-red-600">Failed to load graph: {err}</p>}
        {!err && !data && <p className="p-4 text-sm text-muted-foreground">Loading graph…</p>}
        {!err && data && (
          <ForceGraph2D
            width={dims.w}
            height={dims.h}
            graphData={data}
            backgroundColor="#ffffff"
            nodeLabel={(n: GNode) => n.label}
            nodeRelSize={5}
            nodeVal={(n: GNode) => (n.kind === "folder" ? 6 : 2)}
            nodeColor={(n: GNode) => (n.kind === "folder" ? "#0ea5e9" : "#94a3b8")}
            linkColor={(l: GLink) => (l.kind === "xref" ? "rgba(200,90,62,0.55)" : "rgba(148,163,184,0.22)")}
            linkWidth={(l: GLink) => (l.kind === "xref" ? 1.5 : 0.5)}
            linkDirectionalParticles={(l: GLink) => (l.kind === "xref" ? 3 : 0)}
            linkDirectionalParticleWidth={2}
            linkDirectionalParticleColor={() => "#c85a3e"}
            cooldownTicks={140}
            onNodeClick={(n: GNode) => {
              if (n.kind === "page") navigate("/" + n.id);
            }}
          />
        )}
      </div>
    </div>
  );
}
